/**
 * Quick view (multi-variant) + quick add (single variant) for product cards.
 */
(function () {
  const SEL = {
    panel: "[js-quick-view-panel]",
    fragment: "#QuickViewFragment",
    triggerOpen: "[js-quick-view-card-trigger]",
  };

  function initYotpoIfPresent() {
    if (typeof yotpoWidgetsContainer !== "undefined" && yotpoWidgetsContainer.initWidgets) {
      try {
        yotpoWidgetsContainer.initWidgets();
      } catch (e) {
        console.warn("Yotpo init", e);
      }
    }
  }

  function reexecuteScripts(container) {
    container.querySelectorAll("script").forEach((oldScript) => {
      if (oldScript.type === "application/json" || oldScript.src) return;
      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function getSearchDrawer() {
    return document.getElementById("SearchDrawer");
  }

  /**
   * Quick-view modals inside a closed drawer stay `display:none` with their parent.
   * Close any that were left open when the search drawer was dismissed.
   */
  function closeStuckNestedFocusableWidgets() {
    const searchDrawer = getSearchDrawer();
    if (!searchDrawer || searchDrawer.classList.contains("open")) return;
    searchDrawer.querySelectorAll("[data-focusable-widget].open").forEach((widget) => {
      if (typeof widget.close === "function") widget.close();
    });
  }

  function openCartDrawerPanel() {
    const cartDrawer = document.querySelector("#CartDrawer");
    if (!cartDrawer || typeof cartDrawer.open !== "function") return;
    if (cartDrawer.getAttribute("aria-hidden") !== "true") return;
    closeStuckNestedFocusableWidgets();
    cartDrawer.open();
  }

  async function loadQuickView(productUrl, modal) {
    if (!modal) return;
    if (!modal.dataset.qvClearBound) {
      modal.dataset.qvClearBound = "true";
      modal.addEventListener("close", function () {
        const panel = modal.querySelector(SEL.panel);
        if (panel) panel.innerHTML = "";
      });
    }
    const panel = modal.querySelector(SEL.panel);
    if (!panel) return;

    const cleanUrl = productUrl.split("?")[0];
    const fetchUrl = cleanUrl + "?section_id=quick-view";

    panel.innerHTML =
      '<div class="quick-view-modal__loading" aria-busy="true" aria-live="polite">' +
      '<loading-spinner loading></loading-spinner>' +
      "</div>";
    ensureQuickViewModalPortaled(modal);
    closeStuckNestedFocusableWidgets();
    modal.open();

    try {
      const res = await fetch(fetchUrl, { headers: { Accept: "text/html" } });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const fragment = doc.querySelector(SEL.fragment);
      if (!fragment) {
        panel.innerHTML = '<p class="text-red text-14 p-xs">Could not load product.</p>';
        return;
      }
      panel.innerHTML = "";
      while (fragment.firstChild) {
        panel.appendChild(fragment.firstChild);
      }
      reexecuteScripts(panel);
      queueMicrotask(() => initYotpoIfPresent());
    } catch (err) {
      console.error(err);
      panel.innerHTML = '<p class="text-red text-14 p-xs">Something went wrong. Please try again.</p>';
    }
  }

  function releaseQuickAddButton(button) {
    if (!button) return;
    button.removeAttribute("disabled");
    const sp = button.querySelector("loading-spinner");
    if (sp) sp.removeAttribute("loading");
  }

  async function quickAddToCart(variantId, button) {
    if (!variantId) return;
    const limits = window.theme && theme.cartQuantityLimits;
    if (button) {
      button.setAttribute("disabled", "disabled");
      const sp = button.querySelector("loading-spinner");
      if (sp) sp.setAttribute("loading", "");
    }
    if (limits && typeof limits.canAddItems === "function") {
      try {
        const cartUrl = ((window.routes && window.routes.cart_url) || "/cart") + ".js";
        const cartRes = await fetch(cartUrl, { credentials: "same-origin" });
        const cartJson = await cartRes.json();
        const isSale = button && button.dataset.variantOnSale === "true";
        const check = limits.canAddItems(cartJson, variantId, 1, isSale);
        if (!check.ok) {
          limits.showDrawerCartError(check.reason);
          openCartDrawerPanel();
          releaseQuickAddButton(button);
          return;
        }
        if (typeof limits.clearDrawerCartError === "function") limits.clearDrawerCartError();
      } catch (err) {
        console.error(err);
      }
    }
    const cartDrawerEl = document.querySelector("cart-drawer");
    const addUrl =
      (window.routes && window.routes.cart_add_url) ||
      ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/") + "cart/add.js";
    const sectionIds =
      cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === "function"
        ? cartDrawerEl.getSectionsToRender().map((e) => e.id)
        : [];
    const payload = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionIds,
      sections_url: window.location.pathname,
    };
    fetch("" + addUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.status) {
          if (window.theme && theme.utils && theme.utils.subscriptions && window.PUB_SUB_EVENTS) {
            theme.utils.subscriptions.publish(window.PUB_SUB_EVENTS.cartError, {
              source: "quick-add",
              errors: data.errors || data.description,
              message: data.message,
            });
          }
          throw new Error(typeof data.description === "string" ? data.description : "Cart error");
        }
        if (data && data.errors) {
          throw new Error(typeof data.errors === "string" ? data.errors : JSON.stringify(data.errors));
        }
        if (!cartDrawerEl || typeof cartDrawerEl.renderContents !== "function") {
          window.location.href =
            (window.routes && window.routes.cart_url) || "/cart";
          return;
        }
        if (document.querySelector("cart-items")) document.querySelector("cart-items").onCartUpdate();
        if (document.querySelector("cart-drawer-items")) document.querySelector("cart-drawer-items").onCartUpdate();
        cartDrawerEl.renderContents(data);
        if (window.theme && theme.utils && theme.utils.subscriptions && window.PUB_SUB_EVENTS) {
          theme.utils.subscriptions.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: "quick-add",
            productVariantId: String(variantId),
          });
        }
        openCartDrawerPanel();
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        releaseQuickAddButton(button);
      });
  }

  /**
   * Reparent modals to <body> so position:fixed is always viewport-relative (avoids
   * transformed ancestors on product grids and nested drawers). Open triggers stay in the card.
   */
  function moveQuickViewModalsToBody(root) {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;
    scope.querySelectorAll("s-modal.quick-view-modal[data-qv-modal-root]").forEach((modal) => {
      if (modal.dataset.qvPortalDone) return;
      modal.dataset.qvPortalDone = "true";
      document.body.appendChild(modal);
    });
  }

  function ensureQuickViewModalPortaled(modal) {
    if (!modal) return;
    if (modal.parentElement === document.body) {
      modal.dataset.qvPortalDone = "true";
      return;
    }
    modal.dataset.qvPortalDone = "true";
    document.body.appendChild(modal);
  }

  function getQuickViewPortalRoots() {
    return [
      document.getElementById("MainContent"),
      document.getElementById("pageContainer"),
      document.querySelector("main"),
      getSearchDrawer(),
    ].filter(Boolean);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", moveQuickViewModalsToBody);
  } else {
    moveQuickViewModalsToBody();
  }

  let qvPortalRaf = false;
  function scheduleMoveQuickViewModalsToBody() {
    if (qvPortalRaf) return;
    qvPortalRaf = true;
    requestAnimationFrame(() => {
      qvPortalRaf = false;
      moveQuickViewModalsToBody();
    });
  }
  getQuickViewPortalRoots().forEach((root) => {
    new MutationObserver(scheduleMoveQuickViewModalsToBody).observe(root, { childList: true, subtree: true });
  });

  document.addEventListener(
    "click",
    function (e) {
      const btn = e.target.closest(SEL.triggerOpen);
      if (!btn) return;

      const mode = btn.getAttribute("data-quick-view-mode");
      const productUrl = btn.getAttribute("data-product-url");
      const variantId = btn.getAttribute("data-variant-id");

      if (mode === "quick-add") {
        e.preventDefault();
        e.stopPropagation();
        quickAddToCart(variantId, btn);
        return;
      }

      if (mode === "pdp" && productUrl) {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign(productUrl);
        return;
      }

      if (mode === "quick-view" && productUrl) {
        e.preventDefault();
        e.stopPropagation();
        const modalId = btn.getAttribute("data-quick-view-modal");
        const modal = modalId ? document.getElementById(modalId) : null;
        if (!modal) {
          console.warn("Quick view: missing modal for id", modalId);
          return;
        }
        ensureQuickViewModalPortaled(modal);
        loadQuickView(productUrl, modal);
      }
    },
    true,
  );

  /** Family size links (same PDP markup): close quick view first, then full navigation. */
  document.addEventListener(
    "click",
    function (e) {
      const link = e.target.closest("[js-pdp-swatch-link]");
      if (!link || !link.href) return;
      const modal = link.closest("s-modal.quick-view-modal");
      if (!modal || !modal.classList.contains("open")) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      const url = link.getAttribute("href");
      if (typeof modal.close === "function") modal.close();
      if (url) window.location.assign(url);
    },
    true,
  );
})();
